// NEVANU — Native Plugin Bridge
// Capacitor-Plugins für iOS/Android. Graceful Degradation im Web.
// Prüft automatisch ob die App als Native oder Web läuft.

import { Capacitor } from '@capacitor/core';

/** Prüft ob die App als native Capacitor-App läuft */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Gibt die aktuelle Plattform zurück: 'ios', 'android', oder 'web' */
export function getPlatform(): string {
  return Capacitor.getPlatform();
}

// ==============================
// Push Notifications
// ==============================

/** Push-Benachrichtigungen registrieren (nur native) */
export async function registerPushNotifications(): Promise<void> {
  if (!isNative()) {
    console.log('[Native] Push: Web-Modus — nutze Web Push API');
    return;
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Berechtigung anfragen
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      console.warn('[Native] Push-Berechtigung verweigert');
      return;
    }

    // Registrieren
    await PushNotifications.register();

    // Token empfangen
    PushNotifications.addListener('registration', (token) => {
      console.log('[Native] Push-Token:', token.value);
      // TODO: Token an Server senden für serverseitige Benachrichtigungen
      // api('push', 'register', { token: token.value, platform: getPlatform() });
    });

    // Fehler
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[Native] Push-Registrierung fehlgeschlagen:', error);
    });

    // Notification empfangen (App im Vordergrund)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Native] Push empfangen:', notification.title);
      // In-App-Toast anzeigen
      if ((window as any).showToast) {
        (window as any).showToast(notification.title || 'Neue Benachrichtigung', 'info');
      }
    });

    // Notification angeklickt
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Native] Push-Aktion:', action.notification.data);
      // Navigation basierend auf Notification-Daten
      const data = action.notification.data;
      if (data?.tab) {
        if ((window as any).bottomNavGo) {
          (window as any).bottomNavGo(data.tab);
        }
      }
    });

    console.log('[Native] Push Notifications registriert');
  } catch (e) {
    console.warn('[Native] Push nicht verfügbar:', e);
  }
}

// ==============================
// Kamera
// ==============================

/** Foto aufnehmen oder aus Galerie wählen */
export async function takePhoto(): Promise<string | null> {
  if (!isNative()) {
    // Web-Fallback: File Input
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        } else {
          resolve(null);
        }
      };
      input.click();
    });
  }

  try {
    const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const image = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt, // Kamera oder Galerie wählen lassen
      width: 1200,
      height: 1200,
      correctOrientation: true,
    });
    return image.dataUrl || null;
  } catch (e) {
    console.warn('[Native] Kamera-Fehler:', e);
    return null;
  }
}

// ==============================
// Haptics (Vibrieren)
// ==============================

/** Leichtes haptisches Feedback */
export async function hapticLight(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {}
}

/** Mittleres haptisches Feedback */
export async function hapticMedium(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch {}
}

/** Erfolgreiches haptisches Feedback */
export async function hapticSuccess(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch {}
}

/** Fehler haptisches Feedback */
export async function hapticError(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Error });
  } catch {}
}

// ==============================
// Status Bar
// ==============================

/** Status Bar Farbe setzen (iOS/Android) */
export async function setStatusBarColor(color: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar } = await import('@capacitor/status-bar');
    await StatusBar.setBackgroundColor({ color });
  } catch {}
}

/** Status Bar Stil (hell/dunkel) setzen */
export async function setStatusBarStyle(dark: boolean): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
  } catch {}
}

// ==============================
// App Lifecycle
// ==============================

/** App-Lifecycle Events registrieren */
export async function registerAppLifecycle(): Promise<void> {
  if (!isNative()) return;

  try {
    const { App } = await import('@capacitor/app');

    // App in den Vordergrund → Daten synchronisieren
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        console.log('[Native] App aktiv — Sync starten');
        if ((window as any).nevanuOffline?.syncAll) {
          (window as any).nevanuOffline.syncAll();
        }
      }
    });

    // Back-Button (Android)
    App.addListener('backButton', () => {
      // Prüfe ob ein Bottom-Sheet offen ist
      const sheets = document.querySelectorAll('.bottom-sheet.active, .modal.active');
      if (sheets.length > 0) {
        // Schliesse das letzte offene Sheet
        const last = sheets[sheets.length - 1];
        (last as HTMLElement).classList.remove('active');
      } else {
        // Navigiere zurück zum Home
        if ((window as any).bottomNavGo) {
          (window as any).bottomNavGo('home');
        }
      }
    });

    // Deep Links
    App.addListener('appUrlOpen', (data) => {
      console.log('[Native] Deep Link:', data.url);
      // z.B. nevanu://kalender → Tab wechseln
      const url = new URL(data.url);
      const tab = url.pathname.replace('/', '') || url.searchParams.get('tab');
      if (tab && (window as any).bottomNavGo) {
        (window as any).bottomNavGo(tab);
      }
    });

    console.log('[Native] App-Lifecycle registriert');
  } catch (e) {
    console.warn('[Native] App-Lifecycle nicht verfügbar:', e);
  }
}

// ==============================
// Keyboard
// ==============================

/** Keyboard Events registrieren (für iOS-Scroll-Fix) */
export async function registerKeyboard(): Promise<void> {
  if (!isNative()) return;

  try {
    const { Keyboard } = await import('@capacitor/keyboard');

    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      document.body.classList.add('keyboard-open');
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
      document.body.classList.remove('keyboard-open');
    });

    console.log('[Native] Keyboard-Handler registriert');
  } catch (e) {
    console.warn('[Native] Keyboard nicht verfügbar:', e);
  }
}

// ==============================
// Initialisierung
// ==============================

/** Alle nativen Plugins initialisieren */
export async function initNativePlugins(): Promise<void> {
  console.log(`[Native] Plattform: ${getPlatform()}, Native: ${isNative()}`);

  if (!isNative()) {
    console.log('[Native] Web-Modus — Native Plugins übersprungen');
    return;
  }

  // Parallel initialisieren
  await Promise.allSettled([
    registerPushNotifications(),
    registerAppLifecycle(),
    registerKeyboard(),
    setStatusBarStyle(false), // Light mode default
  ]);

  console.log('[Native] Alle Plugins initialisiert');
}
